import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Business {
  id: string;
  user_id: string;
  name: string;
  industry: string;
  category: string;
  location: string;
  service_area: string;
  google_profile_url: string | null;
  logo_url: string | null;
  services: string[];
  created_at: string;
  updated_at: string;
}

export function useBusiness(userId: string | undefined) {
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      fetchBusiness();
    } else {
      setLoading(false);
    }
  }, [userId]);

  const fetchBusiness = async () => {
    if (!userId) return;

    try {
      const { data, error } = await supabase
        .from('businesses')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;
      setBusiness(data);
    } catch (err) {
      console.error('Error fetching business:', err);
    } finally {
      setLoading(false);
    }
  };

  const createBusiness = async (businessData: Omit<Business, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!userId) return { error: new Error('Not authenticated') };

    try {
      const { data, error } = await supabase
        .from('businesses')
        .insert({
          ...businessData,
          user_id: userId,
        })
        .select()
        .single();

      if (error) throw error;
      
      setBusiness(data);
      toast.success('Business profile created!');
      return { data };
    } catch (err) {
      const error = err as Error;
      toast.error(error.message);
      return { error };
    }
  };

  const updateBusiness = async (updates: Partial<Business>) => {
    if (!business?.id) return { error: new Error('No business found') };

    try {
      const { data, error } = await supabase
        .from('businesses')
        .update(updates)
        .eq('id', business.id)
        .select()
        .single();

      if (error) throw error;
      
      setBusiness(data);
      toast.success('Business updated!');
      return { data };
    } catch (err) {
      const error = err as Error;
      toast.error(error.message);
      return { error };
    }
  };

  return {
    business,
    loading,
    createBusiness,
    updateBusiness,
    refetch: fetchBusiness,
  };
}
